import React, { useCallback, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { ApolloError } from 'apollo-client';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import { FormFieldData, FormProvider } from 'modules/forms';
import { ReadOn, useAdminRights, usePermissions, UserPermission, WriteOn } from 'modules/permissions';
import { IAppConfig, useConfig } from 'modules/config';
import { useRestClient } from 'modules/rest-client';
import { ActionResponse, ErrorListToastMessage } from 'modules/errors';
import { IFormIsValidFunction } from 'modules/forms';
import GraphQlErrorBoundary from 'components/layout/ErrorBoundary';
import Loading from 'components/layout/Loading';
import { tryParseInt } from 'shared/utils/string.utils';
import { isNullOrUndefined } from 'shared/utils/null.utils';
import AmmendOpportunity from './components/AmmendOpportunityForm';
import OpportunityHeader from './components/OpportunityHeader';
import OpportunitySubHeader from './components/OpportunitySubHeader';
import { ConditionalRender } from 'components/layout/ConditionalRender';
import ViewOpportunityCompletion from './components/OpportunityCompletion';
import OpportunityForbidden from './components/OpportunityForbidden';
import { OpportunityDisplayEnum } from './enums/opportunity-display.enum';
import { GetOpportunityById } from './queries/view-opportunity.queries';
import { GetOpportunityByIdVariables, Opportunity, OpportunityByIdResponse } from './queries/view-opportunity.queries.types';
import { StyledOpportunityContainer } from './styled/ViewAmmendOpportunity.styled';
import { ammendOpportunityOptions } from './options/ammend-opportunity.options';
import { OpportunityEntityMapper } from './mappers/OpportunityEntityMapper';
import {
   UpdateOpportunityResponse,
   UpdateOpportunityVariables,
   IDeleteFileVariables,
   IUpdateFileVariables,
   ReopenOpportunityResponse,
   ReopenOpportunityVariables
} from './mutations/ammend-opportunity.mutations.types';
import { UpdateOpportunity, DeleteFile, UpdateFile, ReopenOpportunity } from './mutations/ammend-opportunity.mutations';
import { UpdateOpportunityRequestMapper } from './mappers/UpdateOpportunityMapper';
import { ObservationStatusEnum } from 'shared/enums/ObservationStatus.enum';
import { useFiles } from 'shared/utils/files/hooks/useFiles';
import { AmmendOpportunityLoadingState } from './enums/ammend-opportunity-loading-state.enum';
import { ObservationFileEntity } from 'shared/types/file/ObservationFileEntity';
import { allowedMimeTypes } from 'shared/options/observation-files.options';
import { mapFileToFormData } from 'shared/utils/mapping.utils';
import { IFileUploadResponse } from 'shared/types/file/IFileUploadRequest';
import { GetObservationPhotoFiles } from 'components/ObservationPhoto/queries/observation-photos.queries';
import { GetObservationPhotoFileVariables } from 'components/ObservationPhoto/queries/observation-photos.queries.types';
import { ObservationFileMapper } from 'shared/mappers/ObservationFileMapper';
import { GetFilesByReferenceResponse } from 'shared/types/file/observation-file.type';
import { FileStatus } from 'shared/enums/FileStatus.enum';
import { getErrorMessages } from 'shared/utils/errors/errors.utils';
import AuditContainer from 'components/AuditContainer';
import { OpportunityEntity } from 'shared/types/domain/OpportunityEntity';
import { englishValidationRules as rules } from 'modules/validation/rules/english.validation-rules';

export const ViewAmmendOpportunity = () => {
   const { settings } = useConfig<IAppConfig>();
   const { params } = useRouteMatch<{ id: string }>();

   const restClient = useRestClient<'fileUpload'>();
   const client = useApolloClient();
   const [isLoading, setIsLoading] = useState<boolean>(true);
   const [loadingMessage, setLoadingMessage] = useState<AmmendOpportunityLoadingState>(
      AmmendOpportunityLoadingState.loadingOpportunity
   );
   const [loadingError, setLoadingError] = useState<ApolloError>();

   const { hasPermission } = usePermissions();
   const { hasAdminRights } = useAdminRights();

   const { addToast } = useToasts();

   const [opportunity, setOpportunity] = useState<Opportunity>();
   const [opportunityForm, setOpportunityForm] = useState<FormFieldData<OpportunityEntity> | undefined>();
   const [updateFileMutation] = useMutation<{ updateDocument: boolean }, IUpdateFileVariables>(UpdateFile);
   const [deleteFileMutation] = useMutation<{ deleteDocument: boolean }, IDeleteFileVariables>(DeleteFile);

   const { files, removeFiles, setData } = useFiles<ObservationFileEntity>({
      allowedMimeTypes,
      onNotAllowed: handleNotAllowedFilesAdded,
      addFile: uploadFile,
      updateFile: updateFile,
      removeFile: deleteFile,
      maxFileUploadBytes: settings?.MaxFileUploadBytes
   });

   function handleNotAllowedFilesAdded(messages: string[]): void {
      addToast(
         <ErrorListToastMessage
            errors={messages}
            baseMessage={'There were disallowed file types in the collection of files uploaded'}
         />,
         { appearance: 'warning' }
      );
   }

   useEffect(() => {
      loadFiles();
   }, [opportunity]);

   const loadFiles = async () => {
      setIsLoading(true);
      if (!opportunity) {
         return;
      }
      try {
         const result = await loadImages(opportunity.uniqueRecordId);

         if (!isNullOrUndefined(result)) {
            const emailedFiles = opportunity.emailedFileIds;
            if (!isNullOrUndefined(emailedFiles)) {
               result.forEach(f => {
                  if (emailedFiles.indexOf(f.fileId as number) !== -1) {
                     f.emailEvidence = true;
                  }
               });
            }

            await setData(result);
         }
      } catch (e) {
         setLoadingError(e as ApolloError);
      } finally {
         setIsLoading(false);
      }
   };

   async function loadImages(uniqueRecordId: UID): Promise<ObservationFileEntity[] | null> {
      try {
         const result = await client.query<GetFilesByReferenceResponse, GetObservationPhotoFileVariables>({
            query: GetObservationPhotoFiles,
            variables: {
               id: uniqueRecordId
            },
            fetchPolicy: 'network-only'
         });
         if (result.errors) return null;

         const mapper = new ObservationFileMapper();
         return mapper.map(result.data);
      } catch (error) {
         setLoadingError(error as ApolloError);
         return null;
      }
   }

   async function uploadFile(
      file: ObservationFileEntity,
      folderName?: string,
      data?: UID
   ): Promise<ObservationFileEntity> {
      const formData = mapFileToFormData<{ referenceId: UID | undefined }>(file, settings, folderName, {
         referenceId: data
      });

      const endpoint = restClient.getEndpoint('fileUpload');
      const result = await endpoint.post<IFileUploadResponse>({
         method: 'UploadFile',
         data: formData,
         onUploadProgress: p => {}
      });

      return { ...file, fileId: result.data.fileId };
   }

   async function updateFile(file: ObservationFileEntity, data: UID): Promise<void> {
      await updateFileMutation({
         variables: {
            request: {
               comment: file.comment ?? '',
               documentTypeId: file.type?.id ?? 6,
               fileId: file.fileId!,
               referenceId: data
            }
         }
      });
   }

   const [displayMode, setDisplayMode] = useState<OpportunityDisplayEnum>(OpportunityDisplayEnum.Overview);

   const setLoadingState = (loadingMessage: AmmendOpportunityLoadingState, isLoading: boolean): void => {
      setLoadingMessage(loadingMessage);
      setIsLoading(isLoading);
   };

   async function deleteFile(file: ObservationFileEntity): Promise<void> {
      await deleteFileMutation({
         variables: {
            referenceId: opportunity?.uniqueRecordId ?? '',
            fileId: file.fileId!
         }
      });
   }

   const loadEntity = useCallback(async (): Promise<void> => {
      const entityId = tryParseInt(params.id);

      if (!isNullOrUndefined(entityId)) {
         setLoadingState(AmmendOpportunityLoadingState.loadingOpportunity, true);

         try {
            let result = await client.query<OpportunityByIdResponse, GetOpportunityByIdVariables>({
               query: GetOpportunityById,
               variables: {
                  id: entityId
               },
               fetchPolicy: 'network-only'
            });


            setOpportunity(result.data.opportunity);

            if (result?.data != null) {
               const mapper = new OpportunityEntityMapper();

               setOpportunityForm(mapper.map(result?.data));
            } else {
               setOpportunityForm(undefined);
               setOpportunity(undefined);
            }
         } catch (e) {
            setLoadingError(e as ApolloError);
         } finally {
            setLoadingState(AmmendOpportunityLoadingState.loadingOpportunity, false);
         }
      }
   }, [params.id, client]);

   const handleSaveChanges = async (
      formData: FormFieldData<OpportunityEntity> | undefined,
      fileData: ObservationFileEntity[] | undefined,
      isValid: IFormIsValidFunction
   ): Promise<ActionResponse> => {
      setLoadingState(AmmendOpportunityLoadingState.savingChanges, true);

      const response = await updateEntity(formData, isValid);

      if (fileData) {
         fileData
            .filter(f => f.fileStatus === FileStatus.Removed)
            .forEach(f => {
               deleteFile(f);
            });
      }

      setLoadingState(AmmendOpportunityLoadingState.savingChanges, false);

      if (!response.successful) {
         addToast(<ErrorListToastMessage errors={response.getErrorMessages()} />, { appearance: 'error' });
      } else {
         addToast('Changes saved successfully', { appearance: 'success' });

         loadEntity();
      }

      return response;
   };

   const updateEntity = async (
      formData: FormFieldData<OpportunityEntity> | undefined,
      isValid: IFormIsValidFunction
   ): Promise<ActionResponse> => {
      const actionResponse = new ActionResponse();

      if (isNullOrUndefined(formData) || !isValid(true)) {
         return actionResponse.set({
            successful: false,
            error: {
               message: 'Some of the information entered was not valid, please fix any validation errors before continuing'
            }
         });
      }

      if (opportunity?.observationStatus !== ObservationStatusEnum.Open) {
         return actionResponse.set({
            successful: false,
            error: {
               message: 'You are attempting to amend a closed near miss which is prohibited.'
            }
         });
      }

      if (
         !hasPermission(WriteOn(UserPermission.ExistingObservation)) &&
         !hasPermission(WriteOn(UserPermission.IncidentDate)) &&
         !hasPermission(WriteOn(UserPermission.ObservationReporter))
      ) {
         return actionResponse.set({
            successful: false,
            error: {
               message: 'You do not have the required permissions to perform this action.'
            }
         });
      }

      const mapper = new UpdateOpportunityRequestMapper();
      const requestObject = mapper.map(formData);

      try {
         await client.mutate<UpdateOpportunityResponse, UpdateOpportunityVariables>({
            mutation: UpdateOpportunity,
            variables: {
               request: requestObject
            }
         });

         actionResponse.set({ successful: true });
      } catch (error) {
         actionResponse.set({
            successful: false,
            error: {
               message: {
                  baseMessage: 'Issue updating Opportunity',
                  messages: getErrorMessages(error as ApolloError)
               }
            }
         });
      }

      return actionResponse;
   };

   const handleComplete = () => {
      loadEntity();
   };

   const handleReOpen = async () => {
      setLoadingState(AmmendOpportunityLoadingState.submittingReOpening, true);
      const actionResponse = new ActionResponse();

      if (opportunity?.observationStatus == ObservationStatusEnum.Open) {
         setLoadingState(AmmendOpportunityLoadingState.submittingReOpening, false);
         return actionResponse.set({
            successful: false,
            error: {
               message: 'The Opportunity is already open'
            }
         });
      }

      if (!hasPermission(WriteOn(UserPermission.ReOpen))) {
         setLoadingState(AmmendOpportunityLoadingState.submittingReOpening, false);
         return actionResponse.set({
            successful: false,
            error: {
               message: 'You do not have the required permissions to perform this action.'
            }
         });
      }

      try {
         await client.mutate<ReopenOpportunityResponse, ReopenOpportunityVariables>({
            mutation: ReopenOpportunity,
            variables: {
               nearMissId: opportunity?.observationId ?? 0
            }
         });

         loadEntity();

         actionResponse.set({ successful: true });
      } catch (error: any) {
         actionResponse.set({
            successful: false,
            error: {
               message: 'There was an issue re-opening the Opportunity',
               error: error
            }
         });
      }

      const response = actionResponse;

      setLoadingState(AmmendOpportunityLoadingState.submittingReOpening, false);

      if (!response.successful) {
         addToast(<ErrorListToastMessage errors={response.getErrorMessages()} />, { appearance: 'error' });
      } else {
         addToast('Changes saved successfully', { appearance: 'success' });
      }

      return response;
   };

   useEffect(() => {
      loadEntity();
   }, [loadEntity]);

   const goToOverview = (): void => {
      setDisplayMode(OpportunityDisplayEnum.Overview);
   };

   const goToCompletionDetails = (): void => {
      if (opportunity?.completion) {
         setDisplayMode(OpportunityDisplayEnum.Completion);
      }
   };

   const goToAuditLog = (): void => {
      setDisplayMode(OpportunityDisplayEnum.Audit);
   };

   return (
      <React.Fragment>
         <Loading isLoading={isLoading} message={loadingMessage} noDelay>
            <GraphQlErrorBoundary error={loadingError}>
               <StyledOpportunityContainer>
                  <ConditionalRender condition={hasAdminRights} elseRender={() => <OpportunityForbidden />}>
                     <FormProvider<OpportunityEntity> 
                        options={ammendOpportunityOptions} 
                        formFields={opportunityForm}
                        useEnglish
                     >
                        <OpportunityHeader
                           opportunity={opportunity}
                           onSave={handleSaveChanges}
                           onComplete={handleComplete}
                           onReOpen={handleReOpen}
                           setLoadingState={setLoadingState}
                           isLoading={isLoading}
                           files={files}
                        />

                        <OpportunitySubHeader
                           opportunity={opportunity}
                           displayMode={displayMode}
                           formHasErrors={false}
                           goToOverview={goToOverview}
                           goToCompletionDetails={goToCompletionDetails}
                           goToAuditLog={goToAuditLog}
                        />

                        <ConditionalRender condition={displayMode == OpportunityDisplayEnum.Overview}>
                           <AmmendOpportunity opportunity={opportunity} removeFiles={removeFiles} files={files} />
                        </ConditionalRender>

                        <ConditionalRender condition={displayMode === OpportunityDisplayEnum.Completion}>
                           <ViewOpportunityCompletion
                              observationUniqueId={opportunity?.uniqueRecordId ?? ''}
                              opportunityCompletion={opportunity?.completion}
                              files={files.filter(x => x.type?.id === 11)}
                           />
                        </ConditionalRender>

                        <ConditionalRender
                           condition={
                              displayMode == OpportunityDisplayEnum.Audit && hasPermission(ReadOn(UserPermission.Audit))
                           }
                        >
                           {opportunity && <AuditContainer 
                              uniqueRecordId={opportunity.uniqueRecordId}
                              useEnglish={true}
                           />}
                        </ConditionalRender>
                     </FormProvider>
                  </ConditionalRender>
               </StyledOpportunityContainer>
            </GraphQlErrorBoundary>
         </Loading>
      </React.Fragment>
   );
};
